import { useState } from "react";
import { Robot, Copy, CheckCircle, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { debtorApi } from "@/lib/api";
import { toast } from "sonner";
import Spinner from "@/components/Spinner";
import { fmtCurrency as fmt } from "@/lib/format";

const intentColors = {
  "Promesa de Pago": "text-warning",
  "Sin Liquidez": "text-warning",
  "Ya Pagó": "text-primary",
  "Disputa": "text-destructive",
  "Solicitud de Tiempo": "text-foreground/70",
  "Confirmación": "text-success",
  "Otro": "text-muted-foreground",
};

export default function ReplyAnalyzer({ debtor, onAfterAnalyze }) {
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const handleAnalyze = async () => {
    if (!reply.trim()) return toast.warning("Ingrese la respuesta del deudor");
    setLoading(true);
    try {
      const r = await debtorApi.analyzeReply(debtor.id, reply);
      setAnalysis(r);
      toast.success(`Intención detectada: ${r.intent}`);
      onAfterAnalyze?.();
    } catch {
      toast.error("Error analizando respuesta");
    } finally {
      setLoading(false);
    }
  };

  const copyResponse = () => {
    navigator.clipboard.writeText(analysis.response);
    toast.success("Respuesta copiada");
  };

  return (
    <div className="space-y-4" data-testid="reply-analyzer">
      <div className="bg-card border border-border rounded-md p-5 space-y-4">
        <div>
          <div className="overline mb-2">Respuesta del deudor</div>
          <p className="text-sm text-muted-foreground mb-3">
            Pegue aquí la respuesta recibida del deudor (Email o WhatsApp). El Negociador
            Conversacional detectará la intención y sugerirá una respuesta adecuada.
          </p>
          <Textarea
            data-testid="reply-input"
            className="min-h-[140px]"
            placeholder="Ej: 'Buenas tardes, le agradezco su gestión. Voy a poder transferir el lunes 15…'"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
          />
        </div>
        <Button onClick={handleAnalyze} disabled={loading} data-testid="analyze-reply-btn">
          {loading ? (
            <Spinner size="s" className="mr-2" />
          ) : (
            <Robot size={16} weight="duotone" className="mr-2" />
          )}
          {loading ? "Analizando…" : "Analizar respuesta"}
        </Button>
      </div>

      {analysis && (
        <div
          className="bg-card border border-border rounded-md overflow-hidden animate-fade-in"
          data-testid="analysis-result"
        >
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <div>
              <div className="overline">Intención detectada</div>
              <div
                className={`font-heading font-semibold text-lg mt-0.5 ${
                  intentColors[analysis.intent] || "text-muted-foreground"
                }`}
              >
                {analysis.intent}
              </div>
            </div>
            {analysis.escalate ? (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-destructive/10 text-destructive text-xs font-medium">
                <Warning size={14} weight="duotone" /> Escalar a humano
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-success/10 text-success text-xs font-medium">
                <CheckCircle size={14} weight="duotone" /> Resuelto por IA
              </div>
            )}
          </div>
          <div className="p-5 ai-message">
            <div className="overline mb-2">Respuesta sugerida</div>
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{analysis.response}</p>
            {(analysis.promesa_monto || analysis.promesa_fecha) && (
              <div className="mt-4 grid grid-cols-2 gap-3 p-3 bg-warning/10 rounded-md text-xs">
                {analysis.promesa_monto && (
                  <div data-testid="promesa-monto">
                    <div className="overline">Monto comprometido</div>
                    <div className="font-mono font-semibold text-sm mt-1">
                      {fmt(analysis.promesa_monto)}
                    </div>
                  </div>
                )}
                {analysis.promesa_fecha && (
                  <div data-testid="promesa-fecha">
                    <div className="overline">Fecha / plan</div>
                    <div className="font-semibold text-sm mt-1">{analysis.promesa_fecha}</div>
                  </div>
                )}
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={copyResponse}
              data-testid="copy-response-btn"
            >
              <Copy size={14} className="mr-2" /> Copiar respuesta
            </Button>
          </div>
        </div>
      )}

      {debtor.last_reply && !analysis && (
        <div className="text-xs text-muted-foreground">
          Última respuesta analizada: <span className="italic">"{debtor.last_reply}"</span> —
          intención: <b>{debtor.last_intent}</b>
        </div>
      )}
    </div>
  );
}
